import { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import * as echarts from 'echarts';
import { VblAnalysisResult } from '@/types/vbl';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';
import { calculateVblBoundaries } from '@/utils/vblBoundaries';

// Type for VBL records in plot data
type PlotVblRecord = {
  // Core identifiers
  failure_id?: string;
  case_id: string;
  // Turbine information
  turbine_id: string;
  turbine_name: string;
  model_id?: string;
  model_name?: string;
  // Site information
  site_id?: string;
  site_name: string;
  // Component information
  component_id?: string;
  component_name?: string;
  major_component_id?: string;
  major_component_name?: string;
  component_major: string;
  component_minor?: string | null;
  // Failure information
  failure_mode_name?: string;
  failure_mode: string;
  // Outcome information
  outcome_id?: string;
  outcome_name: string;
  // Date information
  detected_date: string;
  down_date?: string | null;
  actual_rts_date?: string | null;
  predicted_rts_date?: string | null;
  install_date?: string | null;
  cod_date?: string | null;
  failure_date?: string;
  // Metadata
  created_at?: string;
  updated_at?: string;
  // Computed fields
  degradation_time: number;
  degradation_time_years: number;
};

// Define tooltip parameter types
interface TooltipParams {
  componentType: string;
  seriesName: string;
  value?: [number, number];
  data?: {
    record?: PlotVblRecord;
    originalTime?: number;
    originalProbability?: number;
    count?: number;
    symbolSize?: number;
    caseId?: string;
    turbineName?: string;
    siteName?: string;
    componentMajor?: string;
    componentMinor?: string;
  };
}

// Weibull probability transformation functions
const weibullY = (probability: number): number => {
  return Math.log(-Math.log(1 - probability));
};

const inverseWeibullY = (y: number): number => {
  return 1 - Math.exp(-Math.exp(y));
};

// Color palettes for different categories
const COLOR_PALETTES = {
  default: ['#3b82f6'],
  category10: [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'
  ],
  category20: [
    '#3b82f6', '#93c5fd', '#ef4444', '#fca5a5', '#10b981', '#86efac',
    '#f59e0b', '#fde047', '#8b5cf6', '#c4b5fd', '#ec4899', '#f9a8d4',
    '#14b8a6', '#5eead4', '#f97316', '#fdba74', '#06b6d4', '#67e8f9',
    '#84cc16', '#bef264'
  ],
  timePeriod: {
    gradient: ['#dbeafe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8'],
  }
};

// Marker shapes for different categories
const MARKER_SHAPES = ['circle', 'rect', 'triangle', 'diamond', 'pin'];

// Helper function to get size legend data based on size option
const getSizeLegendData = (sizeBy: SizeByOption) => {
  if (sizeBy === 'operationTime') {
    return [
      { name: 'Long Degradation', icon: 'circle' },
      { name: 'Medium Degradation', icon: 'circle' },
      { name: 'Short Degradation', icon: 'circle' }
    ];
  } else if (sizeBy === 'recent') {
    return [
      { name: 'Recent (< 30 days)', icon: 'circle' },
      { name: '30-90 days', icon: 'circle' },
      { name: '90-365 days', icon: 'circle' },
      { name: 'Older (> 1 year)', icon: 'circle' }
    ];
  }
  return [];
};

interface UseVblChartOptionsProps {
  analysis: VblAnalysisResult;
  showConfidenceIntervals: boolean;
  confidenceLevel: number;
  showCensoredData: boolean;
  censoredData?: Array<{ x: number; y: number; time: number; probability: number; caseId?: string }>;
  chartType: 'probability' | 'cdf';
  colorBy?: ColorByOption;
  markerBy?: MarkerByOption;
  sizeBy?: SizeByOption;
  showLegend?: boolean;
}

export const useVblChartOptions = ({
  analysis,
  showConfidenceIntervals,
  confidenceLevel: _confidenceLevel,
  showCensoredData,
  censoredData,
  chartType,
  colorBy = 'none',
  markerBy = 'none',
  sizeBy = 'uniform',
  showLegend = false,
}: UseVblChartOptionsProps) => {
  return useMemo<EChartsOption>(() => {
    const degradationTimes = analysis.plotData.empirical.map(p => p.time);
    const censoredTimes = showCensoredData && censoredData ?
      censoredData.map((d) => d.time) : [];
    const allTimes = [...degradationTimes, ...censoredTimes].filter((t) => t > 0);

    const boundaries = calculateVblBoundaries(allTimes);
    const { dataMin, dataMax, curveMin, curveMax, logMinBoundary, logMaxBoundary } = boundaries;

    const span = dataMax - dataMin;
    let interval: number;
    if (span <= 30) {
      interval = 5;
    } else if (span <= 100) {
      interval = 10;
    } else if (span <= 365) {
      interval = 30;
    } else if (span <= 1000) {
      interval = 100;
    } else {
      interval = 200;
    }

    const minBoundary = Math.floor(dataMin / interval) * interval;
    const maxBoundary = Math.ceil(dataMax / interval) * interval;

    // Helper function to get category value for a record
    const getCategoryValue = (point: Record<string, unknown>, category: ColorByOption | MarkerByOption): string => {
      if (!point) return 'Unknown';
      switch (category) {
        case 'site':
          return (point.siteName as string) || (point.site_name as string) || 'Unknown Site';
        case 'component':
          return (point.componentMajor as string) || (point.component_major as string) || 'Unknown Component';
        case 'subComponent':
          return (point.componentMinor as string) || (point.component_minor as string) || 'Unknown Sub-Component';
        case 'turbine':
          return (point.modelName as string) || (point.model_name as string) || 'Unknown Model';
        case 'outcome':
          return (point.outcomeName as string) || (point.outcome_name as string) || 'Unknown Outcome';
        case 'timePeriod': {
          const dateField = (point.detectedDate as string) || (point.detected_date as string);
          if (dateField) {
            const year = new Date(dateField).getFullYear();
            if (year < 2020) return 'Before 2020';
            if (year < 2022) return '2020-2021';
            if (year < 2024) return '2022-2023';
            return '2024+';
          }
          return 'Unknown Period';
        }
        default:
          return 'Default';
      }
    };

    // Get unique categories for color and marker mapping
    const getUniqueCategories = (option: ColorByOption | MarkerByOption): string[] => {
      if (option === 'none') return ['Default'];
      const categories = new Set<string>();
      analysis.plotData.empirical?.forEach(point => {
        categories.add(getCategoryValue(point, option));
      });
      const sorted = Array.from(categories).sort();
      if (sorted.length > 20) {
        return [...sorted.slice(0, 20), 'Other'];
      }
      return sorted;
    };

    // Create color mapping
    const _getColor = (point: Record<string, unknown>): string => {
      if (colorBy === 'none') return '#3b82f6';
      const category = getCategoryValue(point, colorBy);
      const index = colorCategories.indexOf(category);
      if (index === -1 || index >= 20) return COLOR_PALETTES.category20[20];
      return COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
    };

    // Create marker mapping
    const getMarker = (point: Record<string, unknown>): string => {
      if (markerBy === 'none') return 'circle';
      const category = getCategoryValue(point, markerBy);
      const index = markerCategories.indexOf(category);
      if (index === -1) return 'circle';
      return MARKER_SHAPES[index % MARKER_SHAPES.length];
    };

    // Create size mapping
    const getSize = (point: Record<string, unknown>, index: number): number => {
      const baseSize = 8;
      if (sizeBy === 'uniform') return baseSize;
      if (sizeBy === 'operationTime') {
        const time = degradationTimes[index];
        const normalized = (time - dataMin) / (dataMax - dataMin);
        return baseSize + normalized * 8;
      }
      if (sizeBy === 'recent') {
        const detectedDate = (point.detectedDate as string) || (point.detected_date as string);
        if (detectedDate) {
          const date = new Date(detectedDate);
          const now = new Date();
          const daysAgo = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
          if (daysAgo < 30) return 16;
          if (daysAgo < 90) return 12;
          if (daysAgo < 365) return 10;
        }
        return 6;
      }
      return baseSize;
    };

    // Get unique categories for color and marker mapping - shared between CDF and probability
    const colorCategories = getUniqueCategories(colorBy);
    const markerCategories = getUniqueCategories(markerBy);

    if (chartType === 'cdf') {
      const theoreticalData = analysis.plotData.theoretical
        .filter((point) => point.x >= curveMin && point.x <= curveMax) // Use shared curve boundaries
        .map((point) => [point.x, point.y * 100]);

      const series: echarts.SeriesOption[] = [];

      const needsCategorization = colorBy !== 'none' || markerBy !== 'none' || sizeBy !== 'uniform';

      if (needsCategorization) {
        const createSeriesForCategory = (
          categoryType: string,
          categories: string[],
          isSecondary: boolean = false
        ) => {
          const dataByCategory: Record<string, Array<{
            value: [number, number];
            record: Record<string, unknown>;
            originalTime: number;
            originalProbability: number;
            symbol: string;
            symbolSize: number;
            itemStyle: {
              color: string;
              borderColor: string;
              borderWidth: number;
              opacity?: number;
            };
          }>> = {};

          analysis.plotData.empirical.forEach((point, i) => {
            let category: string;
            if (categoryType === 'size') {
              // Special handling for size-based categorization
              const size = getSize(point as Record<string, unknown>, i);
              if (size <= 8) category = 'Small';
              else if (size <= 12) category = 'Medium';
              else category = 'Large';
            } else {
              category = getCategoryValue(point, categoryType as ColorByOption | MarkerByOption);
            }

            const limitedCategory = categories.includes(category) ? category : 'Other';

            if (!dataByCategory[limitedCategory]) {
              dataByCategory[limitedCategory] = [];
            }

            // Determine color for this data point based on category
            let dataPointColor: string;
            if (categoryType === colorBy || (isSecondary && colorBy !== 'none')) {
              // Use color categorization when active OR when this is secondary series
              const actualColorCategory = getCategoryValue(point, colorBy);
              const colorCategoryIndex = colorCategories.indexOf(actualColorCategory);
              dataPointColor = COLOR_PALETTES.category20[colorCategoryIndex % COLOR_PALETTES.category20.length];
            } else {
              dataPointColor = '#3b82f6';
            }

            const opacity = isSecondary ? 0.6 : 1;

            dataByCategory[limitedCategory].push({
              value: [point.x, point.y * 100],
              record: point,
              originalTime: point.time,
              originalProbability: point.probability * 100,
              symbol: getMarker(point as Record<string, unknown>),
              symbolSize: getSize(point as Record<string, unknown>, i),
              itemStyle: {
                color: dataPointColor,
                borderColor: dataPointColor,
                borderWidth: 1,
                opacity: opacity
              }
            });
          });

          // Create series for each category
          categories.forEach((category, index) => {
            const categoryData = dataByCategory[category] || [];
            if (categoryData.length > 0) {
              const seriesName = category;

              const seriesConfig: echarts.SeriesOption = {
                name: seriesName,
                type: 'scatter',
                data: categoryData,
                emphasis: {
                  scale: 1.5,
                },
              };

              if (isSecondary) {
                // Secondary series styling - more subtle
                const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
                seriesConfig.color = color;
                seriesConfig.itemStyle = {
                  color: color,
                  borderColor: color,
                  borderWidth: 1,
                  opacity: 0.6
                };
                seriesConfig.symbol = categoryType === markerBy ?
                  MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle';
                seriesConfig.symbolSize = 6;
              } else {
                // Primary series styling - full visibility
                if (categoryType === colorBy) {
                  const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
                  seriesConfig.color = color;
                  seriesConfig.itemStyle = {
                    color: color,
                    borderColor: color,
                    borderWidth: 1
                  };
                } else {
                  seriesConfig.color = '#3b82f6'; // Series-level color for ECharts fill
                  seriesConfig.itemStyle = {
                    color: '#3b82f6',
                    borderColor: '#3b82f6',
                    borderWidth: 1
                  };
                }

                if (categoryType === markerBy) {
                  seriesConfig.symbol = MARKER_SHAPES[index % MARKER_SHAPES.length];
                }
              }

              series.push(seriesConfig);
            }
          });
        };

        if (colorBy !== 'none') {
          createSeriesForCategory(colorBy, colorCategories, false);
        } else if (markerBy !== 'none') {
          createSeriesForCategory(markerBy, markerCategories, false);
        } else if (sizeBy !== 'uniform') {
          createSeriesForCategory('size', ['Small', 'Medium', 'Large'], false);
        }

        if (colorBy !== 'none' && markerBy !== 'none' && markerBy !== colorBy) {
          createSeriesForCategory(markerBy, markerCategories, true);
        }

        if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
          createSeriesForCategory('size', ['Small', 'Medium', 'Large'], true);
        }
      } else {
        const empiricalData = analysis.plotData.empirical.map((point, _i) => ({
          value: [point.x, point.y * 100],
          record: point,
          originalTime: point.time,
          originalProbability: point.probability * 100,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#3b82f6',
            borderColor: '#3b82f6',
            borderWidth: 1
          }
        }));

        series.push({
          name: 'Empirical CDF',
          type: 'scatter',
          color: '#3b82f6',
          data: empiricalData,
          itemStyle: {
            color: '#3b82f6',
            borderColor: '#3b82f6',
            borderWidth: 1
          },
          emphasis: {
            scale: 1.5,
          },
        });
      }

      series.push({
        name: 'Fitted VBL Weibull CDF',
        type: 'line',
        data: theoreticalData,
        smooth: false,
        showSymbol: false,
        lineStyle: {
          color: '#ef4444',
          width: 2,
        },
      });

      if (showConfidenceIntervals && analysis.plotData.confidenceIntervals) {
        const lowerBounds = analysis.plotData.confidenceIntervals.lower
          .filter((p) => p.x >= curveMin && p.x <= curveMax) // Use shared curve boundaries
          .map((p) => [p.x, p.y * 100]);
        const upperBounds = analysis.plotData.confidenceIntervals.upper
          .filter((p) => p.x >= curveMin && p.x <= curveMax) // Use shared curve boundaries
          .map((p) => [p.x, p.y * 100]);

        if (lowerBounds.length > 0 && upperBounds.length > 0) {
          series.push(
            {
              name: 'Lower CI',
              type: 'line',
              data: lowerBounds,
              showSymbol: false,
              lineStyle: {
                color: 'rgba(59, 130, 246, 0.3)',
                width: 1,
                type: 'dashed',
              },
              silent: true,
              z: 2,
            },
            {
              name: 'Upper CI',
              type: 'line',
              data: upperBounds,
              showSymbol: false,
              lineStyle: {
                color: 'rgba(59, 130, 246, 0.3)',
                width: 1,
                type: 'dashed',
              },
              silent: true,
              z: 2,
            }
          );
        }
      }

      // Calculate dynamic padding based on legend positioning
      const baseBottomPadding = 60;
      const bottomPadding = showLegend ? baseBottomPadding + 30 : baseBottomPadding;
      let rightPadding = 80;
      if (showLegend && needsCategorization) {
        const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;
        const hasSecondaryLegend = colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends;
        const hasTertiaryLegend = (colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform';
        if (hasSecondaryLegend || hasTertiaryLegend) {
          rightPadding = 160;
        }
      } else if (showLegend) {
        rightPadding = 80;
      } else {
        rightPadding = 20;
      }

      const option: EChartsOption = {
        grid: {
          left: 60,
          right: rightPadding,
          top: 35,
          bottom: bottomPadding,
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          name: 'Degradation Time (days)',
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 11,
            color: '#374151',
          },
          min: minBoundary,
          max: maxBoundary,
          axisLabel: {
            formatter: (value: number) => {
              if (value < 10) return value.toFixed(0);
              if (value < 100) return Math.round(value).toString();
              if (value < 1000) return Math.round(value).toString();
              return value.toFixed(0);
            },
            fontSize: 10,
            color: '#6b7280',
          },
          axisLine: {
            lineStyle: {
              color: '#374151',
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#e0e7ff',
              type: 'dashed',
              width: 1,
            },
          },
        },
        yAxis: {
          type: 'value',
          name: 'Cumulative Failure Probability (%)',
          nameLocation: 'middle',
          nameGap: 45,
          nameTextStyle: {
            fontSize: 11,
            color: '#374151',
          },
          min: 0,
          max: 100,
          axisLabel: {
            formatter: '{value}%',
            fontSize: 10,
            color: '#6b7280',
          },
          axisLine: {
            lineStyle: {
              color: '#374151',
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#e0e7ff',
              type: 'dashed',
              width: 1,
            },
          },
        },
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#d1d5db',
          borderWidth: 1,
          borderRadius: 8,
          padding: 0,
          formatter: (params: unknown) => {
            const tooltipParams = params as TooltipParams;
            if (tooltipParams.componentType === 'series' &&
                tooltipParams.seriesName !== 'Fitted VBL Weibull CDF' &&
                tooltipParams.seriesName !== 'Lower CI' &&
                tooltipParams.seriesName !== 'Upper CI') {
              const data = tooltipParams.data;
              if (!data || !data.record) return null;
              const record = data.record;

              return `
                <div style="background: white; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); max-width: 24rem;">
                  <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
                    <p style="font-weight: 600; color: #111827; margin: 0;">Case #${record.case_id || 'N/A'}</p>
                    <p style="font-size: 14px; color: #6b7280; margin: 4px 0 0 0;">${record.turbine_name || record.turbine_id || 'N/A'} • ${record.site_name || 'N/A'}</p>
                  </div>
                  <div style="font-size: 14px;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Degradation Time:</span>
                      <span style="font-weight: 500;">${data.originalTime?.toFixed(0)} days</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Cumulative Probability:</span>
                      <span style="font-weight: 500; color: #3b82f6;">${data.originalProbability?.toFixed(1)}%</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Component:</span>
                      <span style="font-weight: 500;">${record.component_major || 'N/A'}</span>
                    </div>
                    ${
                      record.component_minor
                        ? `
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Sub-component:</span>
                      <span style="font-weight: 500;">${record.component_minor}</span>
                    </div>
                    `
                        : ''
                    }
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Failure Mode:</span>
                      <span style="font-weight: 500;">${record.failure_mode || record.failure_mode_name || 'Unknown'}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Type:</span>
                      <span style="
                        display: inline-flex;
                        padding: 4px 8px;
                        font-size: 12px;
                        font-weight: 600;
                        border-radius: 9999px;
                        ${
                          (record.outcome_name || '').includes('Replace')
                            ? 'background-color: #fee2e2; color: #dc2626;'
                            : (record.outcome_name || '').includes('Planned')
                              ? 'background-color: #dbeafe; color: #1e40af;'
                              : 'background-color: #fef3c7; color: #d97706;'
                        }
                      ">
                        ${record.outcome_name || 'Unknown'}
                      </span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Detection Date:</span>
                      <span style="font-weight: 500;">${record.detected_date ? new Date(record.detected_date).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between;">
                      <span style="color: #6b7280;">Down Date:</span>
                      <span style="font-weight: 500;">${record.down_date ? new Date(record.down_date).toLocaleDateString() : record.actual_rts_date ? new Date(record.actual_rts_date).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                </div>
              `;
            }
            return null;
          },
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: false,
              title: {
                zoom: 'Zoom',
                back: 'Reset',
              },
            },
            restore: {
              title: 'Reset',
            },
            saveAsImage: {
              title: 'Save',
              pixelRatio: 2,
            },
          },
          right: rightPadding,
          top: 10,
        },
        series,
      };

      // Add legend if needed
      if (showLegend) {
        if (needsCategorization) {
          const legends = [];
          const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

          // Primary legend
          if (colorBy !== 'none') {
            const primaryLegendData = colorCategories.map((category, index) => {
              const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
              return {
                name: category,
                icon: shouldCombineLegends ? MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle',
                itemStyle: {
                  color: color,
                  borderColor: color,
                  borderWidth: 1
                },
              };
            });

            legends.push({
              data: primaryLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
            });
          }

          // Secondary Legend: Show marker information when color is primary and not combined
          if (colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends) {
            const markerLegendData = [];
            markerCategories.forEach((category, index) => {
              markerLegendData.push({
                name: category, // Remove prefix like "Component:", "Turbine:", etc.
                icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
                itemStyle: {
                color: '#6b7280',
                borderColor: '#6b7280',
                borderWidth: 1
              }, // Single color for all marker legend items
                textStyle: { color: '#6b7280', fontSize: 11, fontWeight: 'normal' }
              });
            });

            legends.push({
              data: markerLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '20%',
              selector: false,
              itemWidth: 16,
              itemHeight: 12,
              itemGap: 8,
              scrollDataIndex: 0,
              pageButtonItemGap: 4,
              pageButtonGap: 8,
              pageIconSize: 10,
              pageTextStyle: { fontSize: 9 },
              textStyle: { fontSize: 11, color: '#6b7280' }
            });
          }

          // Tertiary Legend: Show size information when not primary
          if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
            const sizeData = getSizeLegendData(sizeBy);
            const tertiaryLegendData = [];
            sizeData.forEach(item => {
              tertiaryLegendData.push({
                name: item.name, // Remove prefix like "Size:"
                icon: 'circle',
                itemStyle: {
                color: '#9ca3af',
                borderColor: '#9ca3af',
                borderWidth: 1
              }, // Single color for all size legend items
                textStyle: { color: '#6b7280', fontSize: 10, fontWeight: 'normal' }
              });
            });

            legends.push({
              data: tertiaryLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '60%',
              selector: false,
              itemWidth: 14,
              itemHeight: 10,
              itemGap: 6,
              scrollDataIndex: 0,
              pageButtonItemGap: 3,
              pageButtonGap: 6,
              pageIconSize: 8,
              pageTextStyle: { fontSize: 8 },
              textStyle: { fontSize: 10, color: '#6b7280' }
            });
          }

          option.legend = legends;
        } else {
          option.legend = {
            type: 'scroll',
            orient: 'horizontal',
            bottom: 5,
            left: 'center',
            itemGap: 15,
            data: series
              .filter(s => s.name && !['Lower CI', 'Upper CI'].includes(s.name as string))
              .map(s => s.name as string),
          };
        }
      }

      return option;
    }

    const theoreticalDataRaw = analysis.plotData.theoretical
      .filter((point) => point.x >= curveMin && point.x <= curveMax);


    const theoreticalData = theoreticalDataRaw.map((point) => {
      const weibullYValue = weibullY(point.y);
      return [point.x, weibullYValue];
    });


    const probTicks = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 30, 50, 70, 80, 90, 95, 99, 99.99];
    const customYValues = probTicks.map((prob) => weibullY(prob / 100));

    const series: echarts.SeriesOption[] = [];

    const needsCategorization = colorBy !== 'none' || markerBy !== 'none' || sizeBy !== 'uniform';

    if (needsCategorization) {
      const createSeriesForCategory = (
        categoryType: string,
        categories: string[],
        isSecondary: boolean = false
      ) => {
        const dataByCategory: Record<string, Array<{
          value: [number, number];
          record: Record<string, unknown>;
          originalTime: number;
          originalProbability: number;
          symbol: string;
          symbolSize: number;
          itemStyle: {
            color: string;
            borderColor: string;
            borderWidth: number;
            opacity?: number;
          };
        }>> = {};

        analysis.plotData.empirical.forEach((point, i) => {
          let category: string;
          if (categoryType === 'size') {
            // Special handling for size-based categorization
            const size = getSize(point as Record<string, unknown>, i);
            if (size <= 8) category = 'Small';
            else if (size <= 12) category = 'Medium';
            else category = 'Large';
          } else {
            category = getCategoryValue(point, categoryType as ColorByOption | MarkerByOption);
          }

          const limitedCategory = categories.includes(category) ? category : 'Other';

          if (!dataByCategory[limitedCategory]) {
            dataByCategory[limitedCategory] = [];
          }

          // Determine color for this data point based on category
          let dataPointColor: string;
          if (categoryType === colorBy || (isSecondary && colorBy !== 'none')) {
            // Use color categorization when active OR when this is secondary series
            const actualColorCategory = getCategoryValue(point, colorBy);
            const colorCategoryIndex = colorCategories.indexOf(actualColorCategory);
            dataPointColor = COLOR_PALETTES.category20[colorCategoryIndex % COLOR_PALETTES.category20.length];
          } else {
            dataPointColor = '#3b82f6';
          }

          const opacity = isSecondary ? 0.6 : 1;

          const jitterOffset = point.x === 1 ? (i % 10) * 0.01 : 0;
          const xValue = point.x + jitterOffset;
          const weibullYValue = weibullY(point.y);

          dataByCategory[limitedCategory].push({
            value: [xValue, weibullYValue],
            record: point,
            originalTime: point.time,
            originalProbability: point.probability * 100,
            symbol: getMarker(point as Record<string, unknown>),
            symbolSize: getSize(point as Record<string, unknown>, i),
            itemStyle: {
              color: dataPointColor,
              borderColor: dataPointColor,
              borderWidth: 1,
              opacity: opacity
            }
          });

        });

        // Create series for each category
        categories.forEach((category, index) => {
          const categoryData = dataByCategory[category] || [];
          if (categoryData.length > 0) {
            const seriesName = category; // Use clean names without prefixes for both primary and secondary

            const seriesConfig: echarts.SeriesOption = {
              name: seriesName,
              type: 'scatter',
              data: categoryData,
              emphasis: {
                scale: 1.5,
              },
            };

            // Apply styling based on whether this is primary or secondary
            if (isSecondary) {
              // Secondary series styling - more subtle
              const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
              seriesConfig.color = color; // Series-level color for ECharts fill
              seriesConfig.itemStyle = {
                color: color,
                borderColor: color,
                borderWidth: 1,
                opacity: 0.6
              };
              seriesConfig.symbol = categoryType === markerBy ?
                MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle';
              seriesConfig.symbolSize = 6; // Smaller for secondary
            } else {
              // Primary series styling - full visibility
              if (categoryType === colorBy) {
                const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
                seriesConfig.color = color;
                seriesConfig.itemStyle = {
                  color: color,
                  borderColor: color,
                  borderWidth: 1
                };
              } else {
                seriesConfig.color = '#3b82f6'; // Series-level color for ECharts fill
                seriesConfig.itemStyle = {
                  color: '#3b82f6',
                  borderColor: '#3b82f6',
                  borderWidth: 1
                };
              }

              if (categoryType === markerBy) {
                seriesConfig.symbol = MARKER_SHAPES[index % MARKER_SHAPES.length];
              }
            }

            series.push(seriesConfig);
          }
        });
      };

      // Create primary series (first active categorization)
      if (colorBy !== 'none') {
        createSeriesForCategory(colorBy, colorCategories, false);
      } else if (markerBy !== 'none') {
        createSeriesForCategory(markerBy, markerCategories, false);
      } else if (sizeBy !== 'uniform') {
        createSeriesForCategory('size', ['Small', 'Medium', 'Large'], false);
      }

      // Create secondary series (additional categorizations)
      if (colorBy !== 'none' && markerBy !== 'none' && markerBy !== colorBy) {
        createSeriesForCategory(markerBy, markerCategories, true);
      }

      if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
        createSeriesForCategory('size', ['Small', 'Medium', 'Large'], true);
      }
    } else {
      // Single series with default styling - ensure log scale safety

      const empiricalData = analysis.plotData.empirical.map((point, _i) => {
        const jitterOffset = point.x === 1 ? (_i % 10) * 0.01 : 0;
        const xValue = point.x + jitterOffset;
        const weibullYValue = weibullY(point.y);


        return {
          value: [xValue, weibullYValue], // Use actual x value, not log
          record: point,
          originalTime: point.time,
          originalProbability: point.probability * 100,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#3b82f6',
            borderColor: '#3b82f6',
            borderWidth: 1
          }
        };
      });

      series.push({
        name: 'VBL Data',
        type: 'scatter',
        color: '#3b82f6', // Series-level color for ECharts fill
        data: empiricalData,
        itemStyle: {
          color: '#3b82f6',
          borderColor: '#3b82f6',
          borderWidth: 1
        },
        emphasis: {
          scale: 1.5,
        },
      });
    }

    // Add fitted line
    series.push({
      name: 'VBL Weibull Fit',
      type: 'line',
      data: theoreticalData,
      smooth: false,
      showSymbol: false,
      lineStyle: {
        color: '#ef4444',
        width: 2,
      },
      silent: true,
    });

    // Add confidence intervals if enabled - use shared curve boundaries

    if (showConfidenceIntervals && analysis.plotData.confidenceIntervals) {
      const lowerBoundsRaw = analysis.plotData.confidenceIntervals.lower
        .filter((p) => p.x >= curveMin && p.x <= curveMax); // Use shared curve boundaries
      const upperBoundsRaw = analysis.plotData.confidenceIntervals.upper
        .filter((p) => p.x >= curveMin && p.x <= curveMax); // Use shared curve boundaries


      const lowerBounds = lowerBoundsRaw.map((p) => [p.x, weibullY(p.y)]);
      const upperBounds = upperBoundsRaw.map((p) => [p.x, weibullY(p.y)]);

      if (lowerBounds.length > 0 && upperBounds.length > 0) {
        // Create confidence band as an area
        const confidenceArea = [
          ...lowerBounds,
          ...upperBounds.reverse()
        ];

        series.push({
          name: 'Confidence Band',
          type: 'line',
          data: confidenceArea,
          showSymbol: false,
          lineStyle: {
            opacity: 0,
          },
          areaStyle: {
            color: 'rgba(59, 130, 246, 0.15)',
          },
          silent: true,
          z: 1,
          tooltip: {
            show: false,
          },
        });

        // Add boundary lines
        series.push(
          {
            name: 'Lower CI',
            type: 'line',
            data: lowerBounds,
            showSymbol: false,
            lineStyle: {
              color: 'rgba(59, 130, 246, 0.3)',
              width: 1,
              type: 'dashed',
            },
            silent: true,
            z: 2,
            tooltip: {
              show: false,
            },
          },
          {
            name: 'Upper CI',
            type: 'line',
            data: upperBounds.reverse(),
            showSymbol: false,
            lineStyle: {
              color: 'rgba(59, 130, 246, 0.3)',
              width: 1,
              type: 'dashed',
            },
            silent: true,
            z: 2,
            tooltip: {
              show: false,
            },
          }
        );
      }
    }

    // Add censored data if available
    if (showCensoredData && censoredData && censoredData.length > 0) {
      const censoredByTime: Record<number, number> = {};
      censoredData.forEach((point) => {
        const time = point.time;
        if (!censoredByTime[time]) censoredByTime[time] = 0;
        censoredByTime[time]++;
      });

      const censoredY = weibullY(0.0015);
      const censoredPoints = Object.entries(censoredByTime).map(([timeStr, count]) => ({
        value: [Math.log(parseFloat(timeStr)), censoredY],
        originalTime: parseFloat(timeStr),
        count: count,
        symbolSize: count === 1 ? 4 : count <= 10 ? 6 : count <= 50 ? 8 : 10,
        itemStyle: {
          color: '#e5e2d8',
          borderColor: '#e5e2d8',
          borderWidth: 1,
          opacity: 0.8
        }
      }));

      series.push({
        name: 'Censored',
        type: 'scatter',
        color: '#e5e2d8', // Series-level color for ECharts fill
        data: censoredPoints,
        symbolSize: (data: { symbolSize: number }) => data.symbolSize,
        itemStyle: {
          color: '#e5e2d8',
          borderColor: '#e5e2d8',
          borderWidth: 1,
          opacity: 0.8,
        },
      });
    }

    // Calculate dynamic padding
    let rightPadding = 80;
    if (showLegend && needsCategorization) {
      const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;
      const hasSecondaryLegend = colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends;
      const hasTertiaryLegend = (colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform';
      if (hasSecondaryLegend || hasTertiaryLegend) {
        rightPadding = 160;
      }
    } else if (showLegend) {
      rightPadding = 80;
    } else {
      rightPadding = 20;
    }

    return {
      grid: {
        left: 60,
        right: rightPadding,
        top: 35,
        bottom: 60,
        containLabel: true,
      },
      xAxis: {
        type: 'log',
        name: 'Degradation Time (days)',
        nameLocation: 'middle',
        nameGap: 25,
        nameTextStyle: {
          fontSize: 11,
          color: '#374151',
        },
        min: logMinBoundary,
        max: logMaxBoundary,
        axisLine: {
          onZero: false,
          lineStyle: {
            color: '#374151',
          },
        },
        position: 'bottom',
        offset: 0,
        logBase: 10,
        // Force logarithmic scale behavior with explicit tick configuration
        interval: 0, // Force to show all major ticks
        splitNumber: 8, // Reasonable number of major divisions
        axisLabel: {
          formatter: (value: number) => {
            // Enhanced formatter for logarithmic scale with better precision
            if (value < 1) return value.toFixed(1);
            if (value < 10) return value.toFixed(1);
            if (value < 100) return Math.round(value).toString();
            if (value < 1000) return Math.round(value).toString();
            return (value >= 1000 ? (value/1000).toFixed(1) + 'k' : value.toFixed(0));
          },
          fontSize: 10,
          color: '#6b7280',
          rotate: 0, // Keep labels horizontal
          margin: 8,
        },
        axisTick: {
          show: true,
          inside: false,
          interval: 0, // Show all major ticks
          length: 6,
        },
        minorTick: {
          show: true,
          splitNumber: 9, // 9 minor ticks between major ticks for proper log scale
          length: 3,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e0e7ff',
            type: 'dashed',
            width: 1,
          },
        },
        minorSplitLine: {
          show: true,
          lineStyle: {
            color: '#f3f4f6',
            type: 'solid',
            width: 0.5,
          },
        },
      },
      yAxis: {
        type: 'value',
        name: 'Failure Probability (%)',
        nameLocation: 'middle',
        nameGap: 45,
        nameTextStyle: {
          fontSize: 11,
          color: '#374151',
        },
        min: weibullY(0.001),
        max: weibullY(0.9999),
        axisLine: {
          lineStyle: {
            color: '#374151',
          },
        },
        axisLabel: {
          customValues: customYValues,
          formatter: (value: number) => {
            const prob = inverseWeibullY(value) * 100;
            let closest = probTicks[0];
            let minDiff = Math.abs(prob - probTicks[0]);
            for (const tick of probTicks) {
              const diff = Math.abs(prob - tick);
              if (diff < minDiff) {
                minDiff = diff;
                closest = tick;
              }
            }
            return `${closest}%`;
          },
          fontSize: 10,
          color: '#6b7280',
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e0e7ff',
            type: 'dashed',
            width: 1,
          },
        },
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#d1d5db',
        borderWidth: 1,
        borderRadius: 8,
        padding: 0,
        formatter: (params: unknown) => {
          const tooltipParams = params as TooltipParams;
          if (tooltipParams.componentType === 'series' &&
              tooltipParams.seriesName !== 'VBL Weibull Fit' &&
              tooltipParams.seriesName !== 'Confidence Band' &&
              tooltipParams.seriesName !== 'Lower CI' &&
              tooltipParams.seriesName !== 'Upper CI' &&
              tooltipParams.seriesName !== 'Censored') {
            const data = tooltipParams.data;
            if (!data || !data.record) return null;
            const record = data.record;

            return `
              <div style="background: white; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); max-width: 24rem;">
                <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
                  <p style="font-weight: 600; color: #111827; margin: 0;">Case #${record.case_id || 'N/A'}</p>
                  <p style="font-size: 14px; color: #6b7280; margin: 4px 0 0 0;">${record.turbine_name || record.turbine_id || 'N/A'} • ${record.site_name || 'N/A'}</p>
                </div>
                <div style="font-size: 14px;">
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Degradation Time:</span>
                    <span style="font-weight: 500;">${data.originalTime?.toFixed(0)} days</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Failure Probability:</span>
                    <span style="font-weight: 500; color: #3b82f6;">${data.originalProbability?.toFixed(1)}%</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Component:</span>
                    <span style="font-weight: 500;">${record.component_major || 'N/A'}</span>
                  </div>
                  ${
                    record.component_minor
                      ? `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Sub-component:</span>
                    <span style="font-weight: 500;">${record.component_minor}</span>
                  </div>
                  `
                      : ''
                  }
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Failure Mode:</span>
                    <span style="font-weight: 500;">${record.failure_mode || record.failure_mode_name || 'Unknown'}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Type:</span>
                    <span style="
                      display: inline-flex;
                      padding: 4px 8px;
                      font-size: 12px;
                      font-weight: 600;
                      border-radius: 9999px;
                      ${
                        (record.outcome_name || '').includes('Replace')
                          ? 'background-color: #fee2e2; color: #dc2626;'
                          : (record.outcome_name || '').includes('Planned')
                            ? 'background-color: #dbeafe; color: #1e40af;'
                            : 'background-color: #fef3c7; color: #d97706;'
                      }
                    ">
                      ${record.outcome_name || 'Unknown'}
                    </span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Detection Date:</span>
                    <span style="font-weight: 500;">${record.detected_date ? new Date(record.detected_date).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between;">
                    <span style="color: #6b7280;">Down Date:</span>
                    <span style="font-weight: 500;">${record.down_date ? new Date(record.down_date).toLocaleDateString() : record.actual_rts_date ? new Date(record.actual_rts_date).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
              </div>
            `;
          }

          // Censored data tooltip
          if (tooltipParams.seriesName === 'Censored') {
            const count = tooltipParams.data?.count || 1;

            return `
              <div style="background: white; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);">
                <div style="font-size: 14px;">
                  <div style="font-weight: 600; color: #111827; margin-bottom: 4px;">Censored VBL Data</div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Time:</span>
                    <span style="font-weight: 500; margin-left: 16px;">${tooltipParams.data?.originalTime?.toFixed(0)} days</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Number of cases:</span>
                    <span style="font-weight: 500; margin-left: 16px;">${count}</span>
                  </div>
                  <div style="font-size: 12px; color: #9ca3af; margin-top: 4px;">Still degrading</div>
                </div>
              </div>
            `;
          }

          return null;
        },
      },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: false,
            title: {
              zoom: 'Zoom',
              back: 'Reset',
            },
          },
          restore: {
            title: 'Reset',
          },
          saveAsImage: {
            title: 'Save',
            pixelRatio: 2,
          },
        },
        right: rightPadding,
        top: 10,
      },
      legend: showLegend ? (() => {
        if (needsCategorization) {
          const legends = [];
          const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

          // Primary legend
          if (colorBy !== 'none') {
            const primaryLegendData = colorCategories.map((category, index) => {
              const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
              return {
                name: category,
                icon: shouldCombineLegends ? MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle',
                itemStyle: {
                  color: color,
                  borderColor: color,
                  borderWidth: 1
                },
                textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
              };
            });

            legends.push({
              data: primaryLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          } else if (markerBy !== 'none') {
            const markerLegendData = markerCategories.map((category, index) => ({
              name: category,
              icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
              textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
            }));

            legends.push({
              data: markerLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          }

          // Secondary Legend: Show marker information when color is primary and not combined
          if (colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends) {
            const markerLegendData = [];
            markerCategories.forEach((category, index) => {
              markerLegendData.push({
                name: category, // Remove prefix like "Component:", "Turbine:", etc.
                icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
                itemStyle: {
                color: '#6b7280',
                borderColor: '#6b7280',
                borderWidth: 1
              }, // Single color for all marker legend items
                textStyle: { color: '#6b7280', fontSize: 11, fontWeight: 'normal' }
              });
            });

            legends.push({
              data: markerLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '20%',
              selector: false,
              itemWidth: 16,
              itemHeight: 12,
              itemGap: 8,
              scrollDataIndex: 0,
              pageButtonItemGap: 4,
              pageButtonGap: 8,
              pageIconSize: 10,
              pageTextStyle: { fontSize: 9 },
              textStyle: { fontSize: 11, color: '#6b7280' }
            });
          }

          // Tertiary Legend: Show size information when not primary
          if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
            const sizeData = getSizeLegendData(sizeBy);
            const tertiaryLegendData = [];
            sizeData.forEach(item => {
              tertiaryLegendData.push({
                name: item.name, // Remove prefix like "Size:"
                icon: 'circle',
                itemStyle: {
                color: '#9ca3af',
                borderColor: '#9ca3af',
                borderWidth: 1
              }, // Single color for all size legend items
                textStyle: { color: '#6b7280', fontSize: 10, fontWeight: 'normal' }
              });
            });

            legends.push({
              data: tertiaryLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '60%',
              selector: false,
              itemWidth: 14,
              itemHeight: 10,
              itemGap: 6,
              scrollDataIndex: 0,
              pageButtonItemGap: 3,
              pageButtonGap: 6,
              pageIconSize: 8,
              pageTextStyle: { fontSize: 8 },
              textStyle: { fontSize: 10, color: '#6b7280' }
            });
          }

          return legends;
        } else {
          return {
            type: 'scroll',
            orient: 'horizontal',
            bottom: 5,
            left: 'center',
            itemGap: 15,
            data: series
              .filter(s => s.name && !['Lower CI', 'Upper CI', 'Confidence Band', 'Censored'].includes(s.name as string))
              .map(s => s.name as string),
          };
        }
      })() : undefined,
      series,
    };
  }, [analysis, showConfidenceIntervals, showCensoredData, censoredData, chartType, colorBy, markerBy, sizeBy, showLegend]);
};
